import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AumicoCoreModule } from '@modeso/aumico-lib-core-fe';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { I18NextModule } from 'angular-i18next';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { QuillModule } from 'ngx-quill';
import { AumicoComponentsModule } from '../../../aumico-components/src/lib';
import { AumicoAnnualReportModule } from '../../../aumico-lib-annualreport-fe/src/public-api';
import { AumicoNotesConfig, AumicoNotesConfigService } from './aumico-notes.config';
import { AddNotesAccordionSectionComponent } from './components/add-notes-accordion-section/add-notes-accordion-section.component';
import { AddNotesComponent } from './components/add-notes-component/add-notes-component.component';
import { ComponentPrintPreviewComponent } from './components/component-print-preview-deprecated/component-print-preview-deprecated.component';
import { ComponentOptionsComponent } from './components/componentOptions/componentOptions.component';
import { InputOptionsFormComponent } from './components/componentOptions/input-options/input-options.component';
import { SuperTableOptionsFormDialogComponent } from './components/componentOptions/super-table-options/super-table-options.component';
import { ConfirmationModal } from './components/confirmation-modal/confirmation-modal.component';
import { EditNotesTypeComponentsTranslationsComponent } from './components/edit-notes-type-components-translations/edit-notes-type-components-translations.component';
import { EditNotesTypeRichTextTranslationsComponent } from './components/edit-notes-type-rich-text-translations/edit-notes-type-rich-text-translations.component';
import { EditNotesTypeTranslationsComponent } from './components/edit-notes-type-translations/edit-notes-type-translations.component';
import { EditNotesTypesTranslationsComponent } from './components/edit-notes-types-translations/edit-notes-types-translations.component';
import { EquityComponent } from './components/equity/equity.component';
import { ExtraSettingsComponent } from './components/extra-settings/extra-settings.component';
import { FliComponentTableComponent } from './components/fli-component-table/fli-component-table.component';
import { FliComponent } from './components/fli/fli.component';
import { FreeTableWithAmountsComponent } from './components/freeTableWithAmounts/freeTableWithAmounts.component';
import { FreeTableWithAmountsComponent2 } from './components/freeTableWithAmounts/freeTableWithAmounts2.component';
import { InputOptionsDialogComponent } from './components/input-options-dialog/input-options-dialog.component';
import { InputComponent } from './components/input/input.component';
import { MovementScheduleTableComponent } from './components/movement-schedule/movement-schedule-table/movement-schedule-table.component';
import { MovementScheduleComponent } from './components/movement-schedule/movement-schedule.component';
import { NotesComponentDisplayComponent } from './components/notes-component-display/notes-component-display.component';
import { NotesComponentsListComponent } from './components/notes-components-list/notes-components-list.component';
import { NotesGermanDisplayComponent } from './components/notes-display/notes-german-display/notes-german-display.component';
import { NotesSwissDisplayComponent } from './components/notes-display/notes-swiss-display/notes-swiss-display.component';
import { NotesEquityDisplayComponent } from './components/notes-equity-display/notes-equity-display.component';
import { NotesInputDisplayComponent } from './components/notes-input-display/notes-input-display.component';
import { NotesMovementScheduleDisplayComponent } from './components/notes-movement-schedule-display/notes-movement-schedule-display.component';
import { NotesReferenceSettingsComponent } from './components/notes-reference-settings/notes-reference-settings.component';
import { NotesSuperTableDisplayComponent } from './components/notes-super-table-display/notes-super-table-display.component';
import { NotesTemplatePreviewComponent } from './components/notesTemplatePreview/notesTemplatePreview.component';
import { PreviewComponent } from './components/preview/preview.component';
import { MovementSchedulePrintPreviewComponent } from './components/print-preview/movement-schedule/movement-schedule-print-preview-deprecated.component';
import { MovementScheduleTablePrintPreviewComponent } from './components/print-preview/movement-schedule/movement-schedule-table-print-preview-deprecated/movement-schedule-print-preview-table-deprecated.component';
import { ReferencingNotesComponent } from './components/referencing-notes/referencing-notes.component';
import { RichTextComponent } from './components/richText/richText.component';
import { SelectExtraSettingsComponent } from './components/select/select-extra-settings/select-extra-settings.component';
import { SelectComponent } from './components/select/select.component';
import { AddColumnComponent } from './components/super-table/components/add-column/add-column.component';
import { AddRowComponent } from './components/super-table/components/add-row/add-row.component';
import { CalculationFunctionSelection } from './components/super-table/components/calculation-function-selection/calculation-function-selection.component';
import { RateSourceSelectionComponent } from './components/super-table/components/rate-source-selection/rate-source-selection.component';
import { MappingItemsSelectionTreeNodeComponent } from './components/super-table/components/mapping-items-selection-tree-node/mapping-items-selection-tree-node.component';
import { MappingItemsSelectionComponent } from './components/super-table/components/mapping-items-selection/mapping-items-selection.component';
import { SuperTableMappingRowSettingsComponent } from './components/super-table/components/mapping-row-settings/mapping-row-settings.component';
import { SuperTableMultiplicationColumnComponent } from './components/super-table/components/multiplication-column/super-table-multiplication-column.component';
import { SuperTableMultiplicationRowComponent } from './components/super-table/components/multiplication-row/super-table-multiplication-row.component';
import { SuperTableRatioColumnComponent } from './components/super-table/components/ratio-column/super-table-ratio-column.component';
import { SuperTableRatioRowComponent } from './components/super-table/components/ratio-row/super-table-ratio-row.component';
import { SuperTableReconcileComponent } from './components/super-table/components/reconcile/reconcile.component';
import { YearCurrencyInputComponent } from './components/super-table/components/year-currency-input/year-currency-input.component';
import { SuperTableSubtractionColumnComponent } from './components/super-table/components/subtraction-column/super-table-subtraction-column.component';
import { SuperTableSubtractionRowComponent } from './components/super-table/components/subtraction-row/super-table-subtraction-row.component';
import { SuperTableSummationColumnComponent } from './components/super-table/components/summation-column/super-table-summation-column.component';
import { SuperTableSummationRowComponent } from './components/super-table/components/summation-row/super-table-summation-row.component';
import { SelectMappingDialogComponent } from './components/super-table/select-mapping-dialog';
import { SuperTableNotesPreviewComponent } from './components/super-table/super-table-notes-preview/super-table-notes-preview.component';
import { SuperTableComponent } from './components/super-table/super-table.component';
import { TableWithFormComponent } from './components/tableWithForm/tableWithForm.component';
import { TitleTranslationsComponent } from './components/title-translations/title-translations.component';
import { SuperTableAddFreeColumnComponent } from './dialogs/super-table-add-free-column-form/super-table-add-free-column-form.dialog';
import { SuperTableCreateSegmentDialogComponent } from './dialogs/super-table-create-segment-form/super-table-create-segment-form.dialog';
import { NotesTemplatesEffects } from './effects/notesTemplates.effects';
import { NotesTypesEffects } from './effects/notesTypes.effects';
import { TranslationsEffects } from './effects/translations.effects';
import { NotesPage } from './pages/notes/notes.page';
import { NotesTemplatesFormPage } from './pages/notesTemplates/notes-templates-form/notes-templates-form.page';
import { NotesTemplatesListPage } from './pages/notesTemplates/notes-templates-list/notes-templates-list.page';
import { NotesTypesFormPage } from './pages/notesTypes/notes-types-form/notes-types-form.page';
import { NotesTypesListPage } from './pages/notesTypes/notes-types-list/notes-types-list.page';
import * as fromReducer from './reducers/notes.reducer';
import { NOTES_UI_FEATURE, uiReducer } from './reducers/ui.reducer';
import { AumicoNotesService } from './services/notes.service';

const EXPORTED_DECLARATIONS = [
    TableWithFormComponent,
    EquityComponent,
    FliComponent,
    FreeTableWithAmountsComponent,
    FreeTableWithAmountsComponent2,
    RichTextComponent,
    SelectComponent,
    InputComponent,
    NotesTemplatesListPage,
    NotesTemplatesFormPage,
    NotesTypesFormPage,
    NotesTypesListPage,
    ComponentOptionsComponent,
    PreviewComponent,
    NotesTemplatePreviewComponent,
    NotesComponentsListComponent,
    NotesPage,
    MovementScheduleTableComponent,
    MovementScheduleComponent,
    MovementScheduleTablePrintPreviewComponent,
    MovementSchedulePrintPreviewComponent,
    ComponentPrintPreviewComponent,
    EditNotesTypesTranslationsComponent,
    EditNotesTypeTranslationsComponent,
    EditNotesTypeComponentsTranslationsComponent,
    EditNotesTypeRichTextTranslationsComponent,
    TitleTranslationsComponent,
    NotesComponentDisplayComponent,
    NotesGermanDisplayComponent,
    NotesSwissDisplayComponent,
    NotesEquityDisplayComponent,
    NotesMovementScheduleDisplayComponent,
    InputOptionsFormComponent,
    NotesInputDisplayComponent,
    SuperTableOptionsFormDialogComponent,
    SuperTableComponent,
    ConfirmationModal,
    AddNotesComponent,
    SuperTableCreateSegmentDialogComponent,
    SuperTableAddFreeColumnComponent,
    NotesSuperTableDisplayComponent,
    AddNotesAccordionSectionComponent,
    SuperTableReconcileComponent,
    MappingItemsSelectionComponent,
    MappingItemsSelectionTreeNodeComponent,
    FliComponentTableComponent,
    AddRowComponent,
    AddColumnComponent,
    SuperTableMappingRowSettingsComponent,
    SelectMappingDialogComponent,
    SuperTableNotesPreviewComponent,
    CalculationFunctionSelection,
    RateSourceSelectionComponent,
    SuperTableSummationRowComponent,
    SuperTableMultiplicationRowComponent,
    SuperTableSubtractionRowComponent,
    SelectMappingDialogComponent,
    SuperTableRatioRowComponent,
    SuperTableSummationColumnComponent,
    SuperTableSubtractionColumnComponent,
    SuperTableRatioColumnComponent,
    SuperTableMultiplicationColumnComponent,
    YearCurrencyInputComponent,
];
@NgModule({
    declarations: [
        EXPORTED_DECLARATIONS,
        SelectExtraSettingsComponent,
        ExtraSettingsComponent,
        InputOptionsDialogComponent,
        NotesReferenceSettingsComponent,
        ReferencingNotesComponent,
        SuperTableReconcileComponent,
    ],
    providers: [],
    imports: [
        CommonModule,
        I18NextModule,
        AumicoCoreModule,
        ReactiveFormsModule,
        StoreModule.forFeature(fromReducer.notesFeatureKey, fromReducer.reducers),
        StoreModule.forFeature(NOTES_UI_FEATURE, uiReducer),

        EffectsModule.forFeature([NotesTemplatesEffects, NotesTypesEffects, TranslationsEffects]),
        MatTabsModule,
        MatTableModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatRadioModule,
        MatButtonModule,
        MatFormFieldModule,
        MatCheckboxModule,
        MatDatepickerModule,
        MatButtonToggleModule,
        MatIconModule,
        MatExpansionModule,
        QuillModule.forRoot({}),
        MatTableModule,
        MatPaginatorModule,
        MatInputModule,
        MatFormFieldModule,
        MatButtonModule,
        MatSelectModule,
        HttpClientModule,
        MatFormFieldModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        FormsModule,
        DragDropModule,
        MatCardModule,
        MatMenuModule,
        NgxMatSelectSearchModule,
        MatDialogModule,
        MatAutocompleteModule,
        MatChipsModule,
        AumicoComponentsModule,
        AumicoAnnualReportModule,
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    exports: [EXPORTED_DECLARATIONS],
})
export class AumicoNotesModule {
    static forRoot(config: AumicoNotesConfig): ModuleWithProviders<AumicoNotesModule> {
        return {
            ngModule: AumicoNotesModule,
            providers: [
                AumicoNotesService,
                {
                    provide: AumicoNotesConfigService,
                    useValue: config,
                },
            ],
        };
    }
}
